/**
 * Fetch PeeringDB colocation / DC facilities within a radius and convert
 * to point SiteLayers. Uses the same proxy + allowlist as the connectivity
 * provider (`www.peeringdb.com`).
 */
import { haversineKm } from '../dataProviders/types';
import { esriFeaturesToSiteLayers, type EsriFeature } from './esriJsonToSiteLayers';
import type { SiteLayer } from '../../types/site';
import { getCatalogEntry } from '../../config/arcgisLayerCatalog';

const MAX_CACHED_ENTRIES = 5000;
const GLOBAL_CACHE_TTL = 4 * 60 * 60 * 1000;

let _facCache: { data: PeeringDbFacility[]; ts: number } | null = null;
let _facFetchPromise: Promise<PeeringDbFacility[]> | null = null;

interface PeeringDbFacility {
  id: number;
  name: string;
  city?: string;
  state?: string;
  country?: string;
  latitude: number;
  longitude: number;
}

async function getFacList(): Promise<PeeringDbFacility[]> {
  if (_facCache && Date.now() - _facCache.ts < GLOBAL_CACHE_TTL) return _facCache.data;
  if (_facFetchPromise) return _facFetchPromise;

  _facFetchPromise = (async () => {
    const urls = [
      'https://www.peeringdb.com/api/fac?country=US&depth=0',
      'https://www.peeringdb.com/api/fac?depth=0',
    ];
    for (const url of urls) {
      try {
        const proxyUrl = `/api/proxy?url=${encodeURIComponent(url)}&accept=application/json`;
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), 30_000);
        const { authedFetch } = await import('../../lib/authedFetch');
        const res = await authedFetch(proxyUrl, {
          signal: controller.signal,
          headers: { Accept: 'application/json' },
        });
        clearTimeout(timer);
        if (!res.ok) continue;
        const text = await res.text();
        if (text.startsWith('<') || !text.startsWith('{')) continue;
        const json = JSON.parse(text) as { data?: Array<Record<string, unknown>> };
        const facs = (json.data ?? [])
          .filter(
            (f): f is Record<string, unknown> =>
              typeof f.latitude === 'number' &&
              typeof f.longitude === 'number' &&
              Number.isFinite(f.latitude) &&
              Number.isFinite(f.longitude),
          )
          .slice(0, MAX_CACHED_ENTRIES)
          .map(f => ({
            id: Number(f.id),
            name: String(f.name ?? 'Facility'),
            city: f.city != null ? String(f.city) : undefined,
            state: f.state != null ? String(f.state) : undefined,
            country: f.country != null ? String(f.country) : undefined,
            latitude: f.latitude as number,
            longitude: f.longitude as number,
          }));
        if (facs.length > 0) {
          _facCache = { data: facs, ts: Date.now() };
          _facFetchPromise = null;
          return facs;
        }
      } catch {
        continue;
      }
    }
    _facFetchPromise = null;
    throw new Error('PeeringDB returned no facility data');
  })();

  _facFetchPromise.catch(() => {
    _facFetchPromise = null;
  });
  return _facFetchPromise;
}

export interface FetchPeeringDbFacilitiesArgs {
  lat: number;
  lng: number;
  radiusMiles: number;
  maxFeatures?: number;
}

export async function fetchPeeringDbFacilities(
  args: FetchPeeringDbFacilitiesArgs,
): Promise<{ layers: SiteLayer[]; count: number; warning?: string }> {
  const entry = getCatalogEntry('peeringdb-facilities');
  if (!entry) throw new Error('PeeringDB catalog entry missing');

  const facs = await getFacList();
  const radiusKm = args.radiusMiles * 1.609344;
  const max = args.maxFeatures ?? 200;

  const nearby = facs
    .map(f => ({
      f,
      distKm: haversineKm(args.lat, args.lng, f.latitude, f.longitude),
    }))
    .filter(x => x.distKm <= radiusKm)
    .sort((a, b) => a.distKm - b.distKm)
    .slice(0, max);

  const esriFeatures: EsriFeature[] = nearby.map(({ f, distKm }) => ({
    geometry: { x: f.longitude, y: f.latitude },
    attributes: {
      name: f.name,
      city: f.city,
      state: f.state,
      country: f.country,
      distance_km: distKm.toFixed(1),
    },
  }));

  const layers = esriFeaturesToSiteLayers(esriFeatures, {
    catalogId: entry.id,
    category: entry.category,
    source: 'peeringdb-import',
    nameField: 'name',
    descriptionFields: ['city', 'state', 'distance_km'],
    labelPrefix: entry.label,
  });

  let warning: string | undefined;
  const totalNearby = facs.filter(
    f => haversineKm(args.lat, args.lng, f.latitude, f.longitude) <= radiusKm,
  ).length;
  if (totalNearby > max) {
    warning = `Showing ${max} of ${totalNearby} facilities within ${args.radiusMiles} mi`;
  }

  return { layers, count: layers.length, warning };
}

/** Test helper — reset module cache between tests. */
export function resetPeeringDbFacilityCacheForTests(): void {
  _facCache = null;
  _facFetchPromise = null;
}
